import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";

const initialState = {
  // Organization Settings
  organizationSettings: null as any,
  organizationSettingsLoading: false,
  organizationSettingsError: null as any,
  
  // Materials
  materials: [] as any[],
  materialsLoading: false,
  materialsError: null as any,
  
  // Invoice Field Mappings
  invoiceFields: null as any,
  invoiceFieldsLoading: false,
  invoiceFieldsError: null as any,
  
  // Users & Roles
  organizationUsers: [] as any[],
  organizationUsersLoading: false,
  organizationUsersError: null as any,
  
  // Organization Roles
  organizationRoles: [] as any[],
  organizationRolesLoading: false,
  organizationRolesError: null as any,
  
  // User Permissions
  userPermissions: {} as any,
  userPermissionsLoading: false,
  userPermissionsError: null as any,
  
  // Expense Categories
  expenseCategories: [] as any[],
  expenseCategoriesLoading: false,
  expenseCategoriesError: null as any,
  
  // General
  isLoading: false,
  isError: false,
  error: null as any,
};

// Organization Settings Actions
export const getOrganizationSettings = createAsyncThunk(
  "settings/getOrganizationSettings",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/organization/settings`);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateOrganizationSettings = createAsyncThunk(
  "settings/updateOrganizationSettings",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...updateData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).put(`/organization/settings`, updateData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Materials Actions
export const getMaterials = createAsyncThunk(
  "settings/getMaterials",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, isActive, search } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // Use the list endpoint with filters
      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/organization/materials/list`, {
        isActive,
        search
      });
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getMaterialsForFrontend = createAsyncThunk(
  "settings/getMaterialsForFrontend",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/organization/materials/frontend`);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMaterial = createAsyncThunk(
  "settings/createMaterial",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...materialData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/organization/materials/create`, materialData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateMaterial = createAsyncThunk(
  "settings/updateMaterial",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...materialData } = params;
      
      if (!organizationId || !materialData.id) {
        return rejectWithValue("Organization ID and Material ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).put(`/organization/materials/update`, materialData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMaterial = createAsyncThunk(
  "settings/deleteMaterial",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, materialId } = params;
      
      if (!organizationId || !materialId) {
        return rejectWithValue("Organization ID and Material ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).delete(`/organization/materials/${materialId}`);
      return fulfillWithValue({ materialId, ...response.data });
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Invoice Fields Actions
export const getInvoiceFields = createAsyncThunk(
  "settings/getInvoiceFields",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/organization/invoice-field-mapping`);
      
      // Transform array response to object keyed by field name
      const mappingsArray = response.data.data || [];
      const mappingsObject: any = {};
      
      mappingsArray.forEach((mapping: any) => {
        mappingsObject[mapping.fieldName] = {
          keywords: mapping.keywords,
          isActive: mapping.isActive,
        };
      });
      
      return fulfillWithValue(mappingsObject);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateInvoiceFields = createAsyncThunk(
  "settings/updateInvoiceFields",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...fieldsData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).put(`/organization/invoice-field-mapping`, fieldsData);
      
      // Transform array response to object keyed by field name
      const mappingsArray = response.data.data || [];
      const mappingsObject: any = {};
      
      mappingsArray.forEach((mapping: any) => {
        mappingsObject[mapping.fieldName] = {
          keywords: mapping.keywords,
          isActive: mapping.isActive,
        };
      });
      
      return fulfillWithValue(mappingsObject);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateInvoiceField = createAsyncThunk(
  "settings/updateInvoiceField",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, fieldName, keywords, isActive } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const updateData: any = {};
      updateData[fieldName] = { keywords, isActive };

      // @ts-ignore
      await axiosApi({ token, organizationId }).put(`/organization/invoice-field-mapping`, updateData);

      return fulfillWithValue({ fieldName, keywords, isActive });
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Expense Categories Actions
export const getExpenseCategories = createAsyncThunk(
  "settings/getExpenseCategories",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/expenses/categories/list`);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createExpenseCategory = createAsyncThunk(
  "settings/createExpenseCategory",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...categoryData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/expenses/categories/create`, categoryData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateExpenseCategory = createAsyncThunk(
  "settings/updateExpenseCategory",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, categoryId, ...categoryData } = params;
      
      if (!organizationId || !categoryId) {
        return rejectWithValue("Organization ID and Category ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).put(`/expenses/categories/${categoryId}`, categoryData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteExpenseCategory = createAsyncThunk(
  "settings/deleteExpenseCategory",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, categoryId } = params;
      
      if (!organizationId || !categoryId) {
        return rejectWithValue("Organization ID and Category ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).delete(`/expenses/categories/${categoryId}`);
      return fulfillWithValue({ categoryId, ...response.data });
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Users Management Actions
export const getOrganizationUsers = createAsyncThunk(
  "settings/getOrganizationUsers",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/user/list`);
      return fulfillWithValue(response.data.data || []);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addUserToOrganization = createAsyncThunk(
  "settings/addUserToOrganization",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...userData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/user/addUserToOrganization`, userData);
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  "settings/updateUserDetails",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...userData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/user/updateUserDetails`, userData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Roles Management Actions  
export const getOrganizationRoles = createAsyncThunk(
  "settings/getOrganizationRoles",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/user/roles/organization/${organizationId}`);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createRole = createAsyncThunk(
  "settings/createRole",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, ...roleData } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      // @ts-ignore - Organization ID is sent via headers, not in the request body
      const response = await axiosApi({ token, organizationId }).post(`/user/roles/create`, roleData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateRole = createAsyncThunk(
  "settings/updateRole",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, roleId, ...roleData } = params;
      
      if (!organizationId || !roleId) {
        return rejectWithValue("Organization ID and Role ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).put(`/user/roles/${roleId}`, roleData);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteRole = createAsyncThunk(
  "settings/deleteRole",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, roleId } = params;
      
      if (!organizationId || !roleId) {
        return rejectWithValue("Organization ID and Role ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).delete(`/user/roles/${roleId}`);
      return fulfillWithValue({ roleId, ...response.data });
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// User Permissions Actions
export const getUserPermissions = createAsyncThunk(
  "settings/getUserPermissions",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, userId } = params;
      
      if (!organizationId || !userId) {
        return rejectWithValue("Organization ID and User ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).get(`/permissions/user/${userId}`);
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const assignRoleToUser = createAsyncThunk(
  "settings/assignRoleToUser",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, userId, roleId } = params;
      
      if (!organizationId || !userId || !roleId) {
        return rejectWithValue("Organization ID, User ID, and Role ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/permissions/assign-role`, {
        userId,
        roleId
      });
      return fulfillWithValue(response.data.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const toggleUserActiveStatus = createAsyncThunk(
  "settings/toggleUserActiveStatus",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, userId } = params;
      
      if (!organizationId || !userId) {
        return rejectWithValue("Organization ID and User ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/user/toggleActive`, {
        userId
      });
      return fulfillWithValue(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeUserFromOrganization = createAsyncThunk(
  "settings/removeUserFromOrganization",
  async (params: any, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, organizationId, userId } = params;
      
      if (!organizationId || !userId) {
        return rejectWithValue("Organization ID and User ID are required");
      }

      // @ts-ignore
      const response = await axiosApi({ token, organizationId }).post(`/user/removeFromOrganization`, {
        userId
      });
      return fulfillWithValue({ ...response.data, userId });
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    resetState: (state) => {
      return initialState;
    },
    clearErrors: (state) => {
      state.organizationSettingsError = null;
      state.materialsError = null;
      state.invoiceFieldsError = null;
      state.expenseCategoriesError = null;
      state.organizationUsersError = null;
      state.organizationRolesError = null;
      state.userPermissionsError = null;
      state.error = null;
      state.isError = false;
    },
  },
  extraReducers: (builder) => {
    // Organization Settings
    builder
      .addCase(getOrganizationSettings.pending, (state) => {
        state.organizationSettingsLoading = true;
        state.organizationSettingsError = null;
      })
      .addCase(getOrganizationSettings.fulfilled, (state, action) => {
        state.organizationSettingsLoading = false;
        state.organizationSettings = action.payload;
      })
      .addCase(getOrganizationSettings.rejected, (state, action) => {
        state.organizationSettingsLoading = false;
        state.organizationSettingsError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Update Organization Settings
    builder
      .addCase(updateOrganizationSettings.pending, (state) => {
        state.organizationSettingsLoading = true;
        state.organizationSettingsError = null;
      })
      .addCase(updateOrganizationSettings.fulfilled, (state, action) => {
        state.organizationSettingsLoading = false;
        state.organizationSettings = action.payload;
      })
      .addCase(updateOrganizationSettings.rejected, (state, action) => {
        state.organizationSettingsLoading = false;
        state.organizationSettingsError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Materials
    builder
      .addCase(getMaterials.pending, (state) => {
        state.materialsLoading = true;
        state.materialsError = null;
      })
      .addCase(getMaterials.fulfilled, (state, action) => {
        state.materialsLoading = false;
        state.materials = action.payload || [];
      })
      .addCase(getMaterials.rejected, (state, action) => {
        state.materialsLoading = false;
        state.materialsError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Create Material
    builder
      .addCase(createMaterial.fulfilled, (state, action) => {
        if (action.payload) {
          state.materials.push(action.payload);
        }
      });

    // Update Material
    builder
      .addCase(updateMaterial.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.materials.findIndex((material: any) => material.id === action.payload.id);
          if (index !== -1) {
            state.materials[index] = action.payload;
          }
        }
      });

    // Delete Material
    builder
      .addCase(deleteMaterial.fulfilled, (state, action) => {
        if (action.payload?.materialId) {
          state.materials = state.materials.filter((material: any) => material.id !== action.payload.materialId);
        }
      });

    // Invoice Fields
    builder
      .addCase(getInvoiceFields.pending, (state) => {
        state.invoiceFieldsLoading = true;
        state.invoiceFieldsError = null;
      })
      .addCase(getInvoiceFields.fulfilled, (state, action) => {
        state.invoiceFieldsLoading = false;
        state.invoiceFields = action.payload;
      })
      .addCase(getInvoiceFields.rejected, (state, action) => {
        state.invoiceFieldsLoading = false;
        state.invoiceFieldsError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Update Invoice Fields
    builder
      .addCase(updateInvoiceFields.fulfilled, (state, action) => {
        state.invoiceFields = action.payload;
      });

    // Update Individual Invoice Field
    builder
      .addCase(updateInvoiceField.fulfilled, (state, action) => {
        if (action.payload && state.invoiceFields) {
          state.invoiceFields[action.payload.fieldName] = {
            keywords: action.payload.keywords,
            isActive: action.payload.isActive,
          };
        }
      });

    // Expense Categories
    builder
      .addCase(getExpenseCategories.pending, (state) => {
        state.expenseCategoriesLoading = true;
        state.expenseCategoriesError = null;
      })
      .addCase(getExpenseCategories.fulfilled, (state, action) => {
        state.expenseCategoriesLoading = false;
        state.expenseCategories = action.payload || [];
      })
      .addCase(getExpenseCategories.rejected, (state, action) => {
        state.expenseCategoriesLoading = false;
        state.expenseCategoriesError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Create Expense Category
    builder
      .addCase(createExpenseCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.expenseCategories.push(action.payload);
        }
      });

    // Update Expense Category
    builder
      .addCase(updateExpenseCategory.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.expenseCategories.findIndex((category: any) => category.id === action.payload.id);
          if (index !== -1) {
            state.expenseCategories[index] = action.payload;
          }
        }
      });

    // Delete Expense Category
    builder
      .addCase(deleteExpenseCategory.fulfilled, (state, action) => {
        if (action.payload?.categoryId) {
          state.expenseCategories = state.expenseCategories.filter((category: any) => category.id !== action.payload.categoryId);
        }
      });

    // Organization Users
    builder
      .addCase(getOrganizationUsers.pending, (state) => {
        state.organizationUsersLoading = true;
        state.organizationUsersError = null;
      })
      .addCase(getOrganizationUsers.fulfilled, (state, action) => {
        state.organizationUsersLoading = false;
        state.organizationUsers = action.payload || [];
      });

    // Add User to Organization - Don't push incomplete data, let the component refresh the list
    builder
      .addCase(addUserToOrganization.fulfilled, (state, action) => {
        // Don't add incomplete data to the array
        // The component should dispatch getOrganizationUsers after successful addition
      });

    // Update User Details
    builder
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.organizationUsers.findIndex((user: any) => user.id === action.payload.id);
          if (index !== -1) {
            state.organizationUsers[index] = action.payload;
          }
        }
      });

    // Organization Roles
    builder
      .addCase(getOrganizationRoles.pending, (state) => {
        state.organizationRolesLoading = true;
        state.organizationRolesError = null;
      })
      .addCase(getOrganizationRoles.fulfilled, (state, action) => {
        state.organizationRolesLoading = false;
        state.organizationRoles = action.payload || [];
      })
      .addCase(getOrganizationRoles.rejected, (state, action) => {
        state.organizationRolesLoading = false;
        state.organizationRolesError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Create Role
    builder
      .addCase(createRole.fulfilled, (state, action) => {
        if (action.payload) {
          state.organizationRoles.push(action.payload);
        }
      });

    // Update Role
    builder
      .addCase(updateRole.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.organizationRoles.findIndex((role: any) => role.id === action.payload.id);
          if (index !== -1) {
            state.organizationRoles[index] = action.payload;
          }
        }
      });

    // Delete Role
    builder
      .addCase(deleteRole.fulfilled, (state, action) => {
        if (action.payload?.roleId) {
          state.organizationRoles = state.organizationRoles.filter((role: any) => role.id !== action.payload.roleId);
        }
      });

    // User Permissions
    builder
      .addCase(getUserPermissions.pending, (state) => {
        state.userPermissionsLoading = true;
        state.userPermissionsError = null;
      })
      .addCase(getUserPermissions.fulfilled, (state, action) => {
        state.userPermissionsLoading = false;
        if (action.payload?.userId) {
          state.userPermissions[action.payload.userId] = action.payload;
        }
      })
      .addCase(getUserPermissions.rejected, (state, action) => {
        state.userPermissionsLoading = false;
        state.userPermissionsError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });

    // Assign Role to User
    builder
      .addCase(assignRoleToUser.fulfilled, (state, action) => {
        // Refresh user permissions after role assignment
        if (action.payload?.userId) {
          state.userPermissions[action.payload.userId] = action.payload;
        }
      });

    // Toggle User Active Status
    builder
      .addCase(toggleUserActiveStatus.fulfilled, (state, action) => {
        // Update the user's active status in the users list
        if (action.payload?.data?.id) {
          const userIndex = state.organizationUsers.findIndex((user: any) => user.id === action.payload.data.id);
          if (userIndex !== -1) {
            state.organizationUsers[userIndex].isActive = action.payload.data.isActive;
            state.organizationUsers[userIndex].updatedAt = action.payload.data.updatedAt;
          }
        }
      });

    // Remove User from Organization
    builder
      .addCase(removeUserFromOrganization.fulfilled, (state, action) => {
        // Remove the user from the users list
        if (action.payload?.userId) {
          state.organizationUsers = state.organizationUsers.filter((user: any) => user.id !== action.payload.userId);
        }
      });

    builder
      .addCase(getOrganizationUsers.rejected, (state, action) => {
        state.organizationUsersLoading = false;
        state.organizationUsersError = action.payload;
        state.isError = true;
        state.error = action.payload;
      });
  },
});

export const { resetState, clearErrors } = settingsSlice.actions;
export default settingsSlice.reducer;